import { ErrorMessage } from '@hookform/error-message';
import { useId } from 'react';
import {
  Control,
  FieldValues,
  RegisterOptions,
  useFormContext,
  UseFormRegisterReturn,
} from 'react-hook-form';

import clsxm from '@/lib/clsxm';

import Typography from '@/components/typography';

import COMMON_CLASS_NAMES from '@/constants/common-class-names';

type ChildrenFunctionProps = UseFormRegisterReturn & {
  id: string;
  hasError: boolean;
  control: Control<FieldValues, unknown>;
};

export type BaseFieldWrapperProps = {
  name: string;
  label?: string | JSX.Element;
  className?: string;
  labelClassName?: string;
  required?: boolean;
};

export type FieldWrapperProps = BaseFieldWrapperProps &
  Pick<RegisterOptions, 'validate' | 'pattern'> & {
    isEmail?: boolean;
    children?:
      | React.ReactNode
      | ((
          props: ChildrenFunctionProps,
          currentValue?: unknown
        ) => React.ReactNode);
  };

export default function FieldWrapper({
  label,
  labelClassName,
  className,
  name,
  children,
  required,
  pattern,
  validate,
  ...props
}: FieldWrapperProps) {
  const id = useId();
  const {
    register,
    formState: { errors },
    watch,
    control,
  } = useFormContext();

  const registerOptions: RegisterOptions = {
    required: required ? 'This field is required' : undefined,
    pattern,
    validate,
  };

  const fieldId = `form-${id}`;
  const hasError = errors && !!errors[name];

  return (
    <div className='w-full'>
      <div
        className={clsxm(
          COMMON_CLASS_NAMES.input,
          COMMON_CLASS_NAMES.inputContainer,
          hasError && COMMON_CLASS_NAMES.inputError,
          className
        )}
      >
        {label && (
          <Typography.Subtitle5
            as='label'
            className={clsxm('text-grey700', labelClassName)}
          >
            {label}
            {required && (
              <>
                &nbsp;
                <span className='text-error'>*</span>
              </>
            )}
          </Typography.Subtitle5>
        )}

        {typeof children === 'function'
          ? children(
              {
                ...register(name, registerOptions),
                hasError,
                id: fieldId,
                control,
                ...props,
              },
              watch(name)
            )
          : children}
      </div>

      <ErrorMessage
        name={name}
        errors={errors}
        render={({ message }) => (
          <Typography.Subtitle5
            className='pl-6 pt-1 text-error'
            data-testid='text-error'
          >
            {message}
          </Typography.Subtitle5>
        )}
      />
    </div>
  );
}
